// Custom date-range popover — the "Custom" sibling of PeriodPicker. The
// title shows the active inclusive [from, to] range; the popover is the same
// Monday-first mini calendar, but selection takes two clicks: the first marks
// the range start, the second completes it (swapped when picked backwards).
// Days outside [min, max-or-now] are disabled — the same occupancy bounds
// the fixed periods enforce.

import type { ReactElement } from 'react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  customRangeTitle,
  MONTH_LONG,
  startOfPeriod,
  WEEKDAY_SHORT,
  type CustomRange,
} from '../lib/meterCharts';
import Icon from './Icon';
import { useDismiss } from './ui';

// Monday-first weekday header (WEEKDAY_SHORT is Sun-indexed).
const MON_FIRST = [1, 2, 3, 4, 5, 6, 0];

const DAY_MS = 24 * 60 * 60 * 1000;

export default function RangePicker({
  range,
  min,
  max,
  now = new Date(),
  onChange,
}: {
  range: CustomRange;
  min?: Date | null;
  max?: Date | null;
  /** "Now" as a wall date on the project calendar (lib/zone.ts). */
  now?: Date;
  onChange: (r: CustomRange) => void;
}): ReactElement {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  // The month the popover is showing (navigation state, not selection).
  const [dispYear, setDispYear] = useState(0);
  const [dispMonth, setDispMonth] = useState(0);
  // First end of an in-progress selection; null = waiting for the start day.
  const [pending, setPending] = useState<Date | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  useDismiss(ref, () => setOpen(false));

  const maxEff = max && max < now ? max : now;

  // A day cell [d, d+1) is selectable iff it overlaps the [min, maxEff] window.
  const dayOk = (d: Date) => (!min || new Date(d.getTime() + DAY_MS) > min) && d <= maxEff;

  const toggle = () => {
    if (!open) {
      setDispYear(range.from.getUTCFullYear());
      setDispMonth(range.from.getUTCMonth());
      setPending(null);
    }
    setOpen((o) => !o);
  };

  const pickDay = (d: Date) => {
    if (!pending) {
      setPending(d);
      return;
    }
    const backwards = d < pending;
    onChange({ from: backwards ? d : pending, to: backwards ? pending : d });
    setPending(null);
    setOpen(false);
  };

  const first = new Date(Date.UTC(dispYear, dispMonth, 1));
  const gridStart = startOfPeriod(first, 'week');
  const cells = Array.from(
    { length: 42 },
    (_, i) =>
      new Date(Date.UTC(gridStart.getUTCFullYear(), gridStart.getUTCMonth(), gridStart.getUTCDate() + i)),
  );
  const prevMonth = new Date(Date.UTC(dispYear, dispMonth - 1, 1));
  const nextMonth = new Date(Date.UTC(dispYear, dispMonth + 1, 1));
  // A month is reachable iff any of its days overlaps the window.
  const monthOk = (start: Date, end: Date) => (!min || end > min) && start <= maxEff;

  // While a selection is in progress only the pending day is marked.
  const selFrom = pending ? pending.getTime() : range.from.getTime();
  const selTo = pending ? pending.getTime() : range.to.getTime();

  return (
    <div className="period-pick" ref={ref}>
      <button className="period-title" onClick={toggle} aria-expanded={open}>
        {customRangeTitle(range)}
      </button>
      {open && (
        <div className="picker-pop">
          <div className="picker-head">
            <button
              className="icon-btn"
              disabled={!monthOk(prevMonth, first)}
              onClick={() => {
                setDispYear(prevMonth.getUTCFullYear());
                setDispMonth(prevMonth.getUTCMonth());
              }}
              aria-label="Previous month"
            >
              <Icon name="chevron-left" size={17} />
            </button>
            <span>
              {MONTH_LONG[dispMonth]} {dispYear}
            </span>
            <button
              className="icon-btn"
              disabled={!monthOk(nextMonth, new Date(Date.UTC(dispYear, dispMonth + 2, 1)))}
              onClick={() => {
                setDispYear(nextMonth.getUTCFullYear());
                setDispMonth(nextMonth.getUTCMonth());
              }}
              aria-label="Next month"
            >
              <Icon name="chevron-right" size={17} />
            </button>
          </div>
          <div className="picker-hint">{t(pending ? 'ui.rangeHintEnd' : 'ui.rangeHintStart')}</div>
          <div className="picker-grid cols-7 weekdays">
            {MON_FIRST.map((d) => (
              <span key={d}>{WEEKDAY_SHORT[d]}</span>
            ))}
          </div>
          <div className="picker-grid cols-7">
            {cells.map((d) => {
              const time = d.getTime();
              const isEnd = time === selFrom || time === selTo;
              const inRange = !isEnd && time > selFrom && time < selTo;
              return (
                <button
                  key={time}
                  className={`picker-cell day${isEnd ? ' selected' : ''}${inRange ? ' in-range' : ''}${
                    d.getUTCMonth() !== dispMonth ? ' faded' : ''
                  }`}
                  disabled={!dayOk(d)}
                  onClick={() => pickDay(d)}
                >
                  {d.getUTCDate()}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
